import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    alpha,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    Tooltip,
    Typography,
} from "@mui/material";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import GradeOutlinedIcon from "@mui/icons-material/GradeOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import { getClassStudents } from "../../redux/sclassRelated/sclassHandle";
import { StyledTableCell, StyledTableRow } from "../../components/styles";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import { useFormTeacherHomeroom } from "../../hooks/useFormTeacherHomeroom";

function StatTile({ label, value, icon: Icon, color = "primary" }) {
    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                borderRadius: 2.5,
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
            }}
        >
            <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                        variant="rounded"
                        sx={{
                            width: 44,
                            height: 44,
                            bgcolor: (t) => alpha(t.palette[color]?.main ?? t.palette.primary.main, 0.1),
                            color: (t) => t.palette[color]?.main ?? t.palette.primary.main,
                        }}
                    >
                        <Icon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {label}
                        </Typography>
                        <Typography variant="h5" fontWeight={800} lineHeight={1.2}>
                            {value}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

function QuickAction({ to, label, description, icon: Icon, color = "primary" }) {
    return (
        <Card
            component={RouterLink}
            to={to}
            elevation={0}
            sx={{
                height: "100%",
                textDecoration: "none",
                color: "inherit",
                borderRadius: 2.5,
                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                transition: (t) => t.transitions.create(["box-shadow", "border-color", "transform"], { duration: 180 }),
                "&:hover": {
                    transform: "translateY(-2px)",
                    borderColor: (t) => alpha(t.palette[color]?.main ?? t.palette.primary.main, 0.4),
                    boxShadow: (t) => `0 8px 24px ${alpha(t.palette.common.black, 0.08)}`,
                },
            }}
        >
            <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar
                        variant="rounded"
                        sx={{
                            bgcolor: (t) => alpha(t.palette[color]?.main ?? t.palette.primary.main, 0.1),
                            color: (t) => t.palette[color]?.main ?? t.palette.primary.main,
                        }}
                    >
                        <Icon fontSize="small" />
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {description}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
}

const TeacherClassDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { sclassStudents, loading } = useSelector((state) => state.sclass);
    const { currentUser } = useSelector((state) => state.user);

    const { activeClassId: subjectClassId, className, subjectName } = useTeacherTeaching();
    const { canEnrollStudents, homeroomClassId, homeroomClassName } = useFormTeacherHomeroom();
    const classID = subjectClassId || homeroomClassId;
    const displayClassName =
        className && className !== "—" ? className : homeroomClassName || "—";
    const schoolName = currentUser?.school?.schoolName;

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        if (classID) dispatch(getClassStudents(classID));
    }, [dispatch, classID]);

    const students = useMemo(
        () => (Array.isArray(sclassStudents) ? sclassStudents : []),
        [sclassStudents]
    );

    const studentRows = useMemo(
        () =>
            students.map((student) => ({
                id: student._id || student.id,
                name: student.name,
                rollNum: student.rollNum,
            })),
        [students]
    );

    const paginatedRows = useMemo(
        () => studentRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [studentRows, page, rowsPerPage]
    );

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3 },
                        mb: 3,
                        borderRadius: 3,
                        color: "common.white",
                        background: (t) =>
                            `linear-gradient(120deg, ${t.palette.grey[900]} 0%, ${t.palette.primary.dark} 50%, ${t.palette.primary.main} 100%)`,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ md: "center" }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                                sx={{
                                    width: 56,
                                    height: 56,
                                    bgcolor: alpha("#fff", 0.14),
                                    fontWeight: 700,
                                }}
                            >
                                <ClassOutlinedIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                                    My class roster
                                </Typography>
                                <Typography variant="h4" fontWeight={800} lineHeight={1.15}>
                                    {displayClassName}
                                </Typography>
                                <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                                    <Chip
                                        size="small"
                                        icon={<MenuBookOutlinedIcon sx={{ color: "inherit !important" }} />}
                                        label={`Subject: ${subjectName}`}
                                        sx={{ bgcolor: alpha("#fff", 0.14), color: "inherit", fontWeight: 600 }}
                                    />
                                    {schoolName ? (
                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={schoolName}
                                            sx={{ borderColor: alpha("#fff", 0.4), color: "inherit" }}
                                        />
                                    ) : null}
                                </Stack>
                            </Box>
                        </Stack>
                        <Button
                            component={RouterLink}
                            to="/Teacher/results"
                            variant="contained"
                            sx={{
                                bgcolor: "common.white",
                                color: "primary.dark",
                                fontWeight: 700,
                                "&:hover": { bgcolor: alpha("#fff", 0.92) },
                            }}
                        >
                            View class results
                        </Button>
                    </Stack>
                </Paper>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        {loading ? (
                            <Skeleton variant="rounded" height={88} />
                        ) : (
                            <StatTile
                                label="Students enrolled"
                                value={students.length}
                                icon={PeopleAltOutlinedIcon}
                                color="primary"
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatTile label="Class" value={displayClassName} icon={ClassOutlinedIcon} color="info" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatTile label="Your subject" value={subjectName} icon={MenuBookOutlinedIcon} color="secondary" />
                    </Grid>
                </Grid>

                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
                    Quick actions
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    {canEnrollStudents ? (
                        <>
                            <Grid item xs={12} sm={4}>
                                <QuickAction
                                    to="/Teacher/enroll-student"
                                    label="Enroll students"
                                    description="Submit new pupils for director verification"
                                    icon={PersonAddAlt1Icon}
                                    color="warning"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <QuickAction
                                    to="/Teacher/form-teacher-results"
                                    label="Class results & reports"
                                    description="Review subject marks and send package to director"
                                    icon={ArticleOutlinedIcon}
                                    color="secondary"
                                />
                            </Grid>
                        </>
                    ) : null}
                    <Grid item xs={12} sm={4}>
                        <QuickAction
                            to="/Teacher/attendance-entry"
                            label="Record attendance"
                            description="Submit daily register for approval"
                            icon={FactCheckOutlinedIcon}
                            color="success"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <QuickAction
                            to="/Teacher/marks-entry"
                            label="Submit subject marks"
                            description="Send marks to your form teacher for review"
                            icon={GradeOutlinedIcon}
                            color="info"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <QuickAction
                            to="/Teacher/results"
                            label="Published results"
                            description="View approved performance data"
                            icon={BarChartOutlinedIcon}
                            color="primary"
                        />
                    </Grid>
                </Grid>

                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2.5,
                        overflow: "hidden",
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    }}
                >
                    <Box
                        sx={{
                            px: 2.5,
                            py: 2,
                            borderBottom: 1,
                            borderColor: "divider",
                            bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                        }}
                    >
                        <Typography variant="h6" fontWeight={700}>
                            Students
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Open a student profile or use the teaching tools above for the whole class.
                        </Typography>
                    </Box>

                    {loading ? (
                        <Box sx={{ p: 2 }}>
                            {[1, 2, 3, 4].map((i) => (
                                <Skeleton key={i} height={48} sx={{ mb: 1 }} />
                            ))}
                        </Box>
                    ) : students.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                            <PeopleAltOutlinedIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                            <Typography color="text.secondary">
                                No students are enrolled in this class yet.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <StyledTableRow>
                                            <StyledTableCell>Roll</StyledTableCell>
                                            <StyledTableCell>Student name</StyledTableCell>
                                            <StyledTableCell align="right">Actions</StyledTableCell>
                                        </StyledTableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedRows.map((row) => (
                                            <StyledTableRow key={row.id} hover>
                                                <StyledTableCell>{row.rollNum}</StyledTableCell>
                                                <StyledTableCell>
                                                    <Typography fontWeight={600}>{row.name}</Typography>
                                                </StyledTableCell>
                                                <StyledTableCell align="right">
                                                    <Stack
                                                        direction="row"
                                                        spacing={0.5}
                                                        justifyContent="flex-end"
                                                    >
                                                        <Tooltip title="View profile">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() =>
                                                                    navigate(`/Teacher/class/student/${row.id}`)
                                                                }
                                                            >
                                                                <VisibilityOutlinedIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                count={studentRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_e, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default TeacherClassDetails;
